import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/data';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 雏田同人馆`;
    } else {
      document.title = '文章未找到 - 雏田同人馆';
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    url: `https://hinata-doujin.readdy.ai/article/${article.id}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hinata-doujin.readdy.ai/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://hinata-doujin.readdy.ai/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap cursor-pointer"
                  >
                    {article.categoryName}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-secondary-100 text-secondary-700 text-xs px-3 py-1 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-line"></i>
                    {article.likes.toLocaleString()} 喜欢
                  </span>
                </div>
              </div>

              <div className="w-full h-[400px] md:h-[500px] rounded-lg overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="prose prose-sm md:prose-base max-w-none">
                <p className="text-base md:text-lg leading-relaxed text-foreground-700 mb-6">
                  <strong>{article.excerpt}</strong>
                </p>

                <div className="text-sm md:text-base leading-relaxed text-foreground-600 space-y-4">
                  <p>
                    {article.content}
                  </p>
                  <p>
                    雏田（日向ヒナタ）作为《火影忍者》中的核心角色之一，以其温柔、坚强、不服输的性格赢得了无数粉丝的喜爱。从最初那个缺乏自信、总是脸红的少女，到后来能够为了保护所爱之人而勇敢站出来的女忍者，雏田的成长轨迹充满了感动与力量。正因为如此，她成为了同人创作中最受欢迎的角色之一，同人画师和作者们从不同的角度诠释着这个角色，创作出了丰富多彩的作品。
                  </p>
                  <p>
                    同人创作（Doujinshi）是动漫文化中不可或缺的一部分。它既是粉丝对原作热爱的表达，也是创作者展现个人风格和想象力的舞台。在雏田的同人创作中，我们可以看到各种各样的题材和风格：有温馨甜蜜的日常故事，有高燃热血的战斗场面，有唯美浪漫的插画作品，还有脑洞大开的平行世界设定。
                  </p>
                  <p>
                    无论你是喜欢雏田的战斗姿态，还是欣赏她的温柔性格，亦或是着迷于她与鸣人之间的感人故事，在这里你都能找到心仪的作品。我们致力于为火影忍者粉丝打造一个高质量的雏田同人作品聚合平台，让每一位热爱雏田的粉丝都能在这里找到属于自己的那份感动。
                  </p>
                  <h2 className="text-xl font-bold font-heading text-foreground-900 mt-8 mb-4">创作风格与题材</h2>
                  <p>
                    雏田的同人创作涵盖了广泛的艺术风格，从传统的水墨画风到现代的数字绘画，从Q版可爱形象到写实风格应有尽有。在题材方面，爱情、战斗、日常、成长是最受欢迎的主题，而不同画师独特的画风和叙事方式则为作品增添了无限可能。
                  </p>
                  <p>
                    特别值得一提的是，许多同人创作者在作品中融入了丰富的日本传统文化元素，如和服、茶道、花道、祭典等，这不仅让作品更具日式美学韵味，也为角色增添了更多层次的文化内涵。
                  </p>
                  <h2 className="text-xl font-bold font-heading text-foreground-900 mt-8 mb-4">社区与文化</h2>
                  <p>
                    围绕雏田同人创作形成了一个充满活力的粉丝社区。创作者们在社交平台上分享作品、交流创作心得，粉丝们则通过点赞、评论和转发表达对作品的喜爱。这种良性的互动不仅促进了更多优秀作品的诞生，也让火影忍者这个经典IP在同人文化的滋养下持续焕发生机。
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 mt-10 pt-8 border-t border-background-200">
                <button className="inline-flex items-center gap-2 px-6 py-2 bg-accent-100 text-accent-700 rounded-full text-sm font-medium hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line"></i>
                  喜欢 ({article.likes.toLocaleString()})
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-2 bg-background-100 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 bg-background-100">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden transition-transform duration-300 hover:-translate-y-1 cursor-pointer"
                    >
                      <div className="w-full h-40 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}